import { useEffect, useRef } from "react";

export const RetentionChartPlaceholder = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Draw the chart on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas dimensions with higher resolution for sharper rendering
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    // Clear canvas
    ctx.clearRect(0, 0, rect.width, rect.height);

    // Calculate points for the exponential curve
    const points = 100;
    const curvePoints = [];

    for (let i = 0; i < points; i++) {
      const progress = i / (points - 1);
      const x = progress * rect.width;

      // Exponential curve: y = 1 * e^(ax) where a is chosen to make y reach 16 when x is 1
      // ln(16) ≈ 2.77, so a ≈ 2.77
      const y = Math.exp(2.77 * progress);
      const yPos = rect.height - (y / 16) * rect.height;

      curvePoints.push({ x, yPos });
    }

    // Draw the red portion of the line (first 5% of the curve)
    ctx.beginPath();
    ctx.setLineDash([]); // Solid line
    ctx.lineWidth = 1;
    ctx.strokeStyle = "#ff7a5c";

    // Start at the beginning of the curve
    ctx.moveTo(curvePoints[0]?.x ?? 0, curvePoints[0]?.yPos ?? 0);

    // Find the point at approximately 5% of the curve
    const redEndIndex = Math.floor(points * 0.05);
    for (let i = 1; i <= redEndIndex; i++) {
      ctx.lineTo(curvePoints[i]?.x ?? 0, curvePoints[i]?.yPos ?? 0);
    }
    ctx.stroke();

    // Draw the red dot at the end of the red line
    const dotX = curvePoints[redEndIndex]?.x ?? 0;
    const dotY = curvePoints[redEndIndex]?.yPos ?? 0;

    ctx.beginPath();
    ctx.arc(dotX, dotY, 4, 0, Math.PI * 2);
    ctx.fillStyle = "white";
    ctx.fill();
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = "#ff7a5c";
    ctx.stroke();

    // Draw the dotted gray line for the rest of the curve
    ctx.beginPath();
    ctx.setLineDash([2, 3]); // Dotted line pattern
    ctx.lineWidth = 1;
    ctx.strokeStyle = "#c5c5c5";

    // Start from where the red line ended
    ctx.moveTo(dotX, dotY);

    // Continue the curve
    for (let i = redEndIndex + 1; i < points; i++) {
      ctx.lineTo(curvePoints[i]?.x ?? 0, curvePoints[i]?.yPos ?? 0);
    }
    ctx.stroke();
  }, []);
  return (
    <div className="mb-8">
      <h2 className="text-lg font-bold text-gray-800 mb-4">Average Retention (days)</h2>
      <div className="h-48 w-full relative">
        {/* Grid lines */}
        <div className="absolute inset-0">
          {/* Horizontal grid lines */}
          {[0, 2, 4, 6, 8, 10, 12, 14, 16].map((value) => (
            <div
              key={value}
              className="absolute w-full border-t border-gray-200"
              style={{
                top: `${100 - (value / 16) * 100}%`,
                left: 0,
                right: 0,
              }}
            />
          ))}
          {/* Vertical grid lines */}
          {[0, 1, 2, 3, 4].map((value) => (
            <div
              key={value}
              className="absolute h-full border-l border-gray-200"
              style={{
                left: `${(value / 4) * 100}%`,
                top: 0,
                bottom: 0,
              }}
            />
          ))}

          {/* Y-axis labels */}
          <div className="absolute right-0 h-full flex flex-col justify-between text-xs text-gray-400 py-1">
            {[16, 14, 12, 10, 8, 6, 4, 2, 0].map((value) => (
              <div key={value}>{value}</div>
            ))}
          </div>
        </div>

        {/* Canvas for chart drawing */}
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full z-10"
          style={{ width: "100%", height: "100%" }}
        />
      </div>
    </div>
  );
};
